import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getNonprofitsState } from "../../../api/Nonprofits";
import StateNonprofitsList from "./StateNonprofitsList";

// Import react-simple-maps components
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Annotation,
} from "react-simple-maps";
import { geoCentroid } from "d3-geo";

// USA GeoJSON with state boundaries
const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// GIS Pin SVG path
const GisPin = () => {
  return (
    <g transform="translate(-12, -24)">
      <path
        d="M12 0C11.4026 -3.32571e-09 10.811 0.117684 10.259 0.346333C9.70703 0.574982 9.2055 0.910116 8.78307 1.3326C8.36063 1.75508 8.02555 2.25664 7.79696 2.80863C7.56836 3.36062 7.45074 3.95223 7.45081 4.54968C7.45077 5.6523 7.8512 6.7174 8.57763 7.54691C9.30405 8.37641 10.307 8.91385 11.4 9.05928V23.4C11.4 23.5591 11.4632 23.7117 11.5757 23.8243C11.6883 23.9368 11.8409 24 12 24C12.1591 24 12.3117 23.9368 12.4243 23.8243C12.5368 23.7117 12.6 23.5591 12.6 23.4V9.05904C13.693 8.91362 14.6959 8.37621 15.4223 7.54676C16.1487 6.7173 16.5492 5.65226 16.5492 4.54968C16.5493 3.95223 16.4316 3.36062 16.2031 2.80863C15.9745 2.25664 15.6394 1.75508 15.2169 1.3326C14.7945 0.910116 14.293 0.574982 13.741 0.346333C13.1891 0.117684 12.5975 -3.32571e-09 12 0ZM10.8403 2.05176C11.0278 2.05176 11.2134 2.08868 11.3865 2.16041C11.5597 2.23213 11.717 2.33727 11.8496 2.4698C11.9821 2.60234 12.0872 2.75968 12.159 2.93284C12.2307 3.10601 12.2676 3.29161 12.2676 3.47904C12.2676 3.66647 12.2307 3.85207 12.159 4.02524C12.0872 4.1984 11.9821 4.35574 11.8496 4.48828C11.717 4.62081 11.5597 4.72595 11.3865 4.79767C11.2134 4.8694 11.0278 4.90632 10.8403 4.90632C10.4618 4.90632 10.0988 4.75595 9.83109 4.48828C9.56342 4.22061 9.41305 3.85758 9.41305 3.47904C9.41305 3.1005 9.56342 2.73747 9.83109 2.4698C10.0988 2.20213 10.4618 2.05176 10.8403 2.05176Z"
        fill="#FFFFFF"
      />
    </g>
  );
};

// Abbreviations mapping for state names
const stateAbbreviations = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  "District of Columbia": "DC",
};

// These states are too small to fit a label inside
const smallStates = ["VT", "NH", "MA", "RI", "CT", "NJ", "DE", "MD", "DC"];

// Return suitable offsets for annotation lines for each small state
const getOffsetForState = (stateAbbr) => {
  const offsets = {
    VT: { dx: -5, dy: -30 },
    NH: { dx: 30, dy: 0 },
    MA: { dx: 40, dy: 0 },
    RI: { dx: 30, dy: 5 },
    CT: { dx: 35, dy: 15 },
    NJ: { dx: 30, dy: 0 },
    DE: { dx: 40, dy: 0 },
    MD: { dx: 40, dy: 20 },
  };
  return offsets[stateAbbr] || { dx: 30, dy: 0 };
};

// States that need position adjustment from their centroid
const adjustedLabelPositions = {
  MI: { dx: 15, dy: -40 },
  LA: { dx: -15, dy: 0 },
  FL: { dx: 18, dy: 0 },
  HI: { dx: -35, dy: -10 },
  CA: { dx: -20, dy: -5 },
  WA: { dx: 0, dy: -10 },
};

// Marker position adjustments for small states
const markerPositions = {
  // Small states need positions within the state boundaries
  VT: { dx: 0, dy: -10 },
  NH: { dx: 0, dy: -10 },
  MA: { dx: -10, dy: 0 },
  RI: { dx: 0, dy: 0 },
  CT: { dx: 0, dy: 0 },
  NJ: { dx: 0, dy: 0 },
  DE: { dx: 0, dy: 0 },
  MD: { dx: 0, dy: 0 },
  DC: { dx: 0, dy: 0 },
  // Inherit the same offsets for regular states
  ...adjustedLabelPositions,
};

export default function UsaMap() {
  const [npStates, setNpStates] = useState([]);
  const [selectedState, setSelectedState] = useState("CA"); // Default select California
  const [mouseDownState, setMouseDownState] = useState(null); // Track state with mouse down
  const navigate = useNavigate();

  useEffect(() => {
    getNonprofitsState()
      .then((response) => {
        setNpStates(response.data || []);
      })
      .catch((error) => {
        console.error(`Could not retrieve list of partners`, error);
        setNpStates([]);
      });
  }, []);

  // Process state data to get counts and colors
  const getStateFills = () => {
    const result = {};

    // Initialize with default values - set all states to #FE994F by default
    Object.values(stateAbbreviations).forEach((abbr) => {
      result[abbr] = { fill: "#FE994F", count: 0 };
    });

    // Count nonprofits per state
    if (Array.isArray(npStates)) {
      npStates.forEach((state) => {
        if (state && state in result) {
          result[state].count++;
        }
      });
    }

    // Apply color coding based on count - these will override the default color
    Object.keys(result).forEach((state) => {
      const count = result[state].count;
      if (count >= 4) {
        result[state].fill = "#FEA667";
      }
      if (count >= 9) {
        result[state].fill = "#FE994F";
      }
      if (count >= 16) {
        result[state].fill = "#FE8834";
      }
      if (count >= 26) {
        result[state].fill = "#FE791B";
      }
    });

    return result;
  };

  const stateFills = getStateFills();

  // Handle state click - memoized to allow sharing between components
  const handleStateClick = useCallback(
    (stateAbbr) => {
      if (!stateAbbr) return;

      // Toggle selected state
      setSelectedState(selectedState === stateAbbr ? null : stateAbbr);
    },
    [selectedState]
  );

  // Handle mouse down and up events
  const handleStateMouseDown = useCallback(
    (stateAbbr) => {
      setMouseDownState(stateAbbr);
      // Immediately update selected state
      handleStateClick(stateAbbr);
    },
    [handleStateClick]
  );

  const handleStateMouseUp = useCallback(() => {
    setMouseDownState(null);
  }, []);

  // Add window mouse up handler to catch mouse releases outside the component
  useEffect(() => {
    const handleWindowMouseUp = () => {
      if (mouseDownState) {
        setMouseDownState(null);
      }
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, [mouseDownState]);

  return (
    <div className="w-full relative min-h-[650px] flex mb-8">
      <div className="flex flex-col lg:flex-row justify-center items-start gap-6 lg:gap-4  xl:pl-16 xl:pr-20 mx-auto flex-grow">
        {/* Nonprofit list - always on left */}
        <div className="lg:order-1 order-2 w-full lg:w-auto mb-6 lg:px-0 px-4  lg:mb-0">
          <StateNonprofitsList selectedState={selectedState} />
        </div>

        {/* Map section - next to list on all screen sizes */}
        <div className="lg:order-2 order-1 w-full lg:flex-1 xl:-mt-24 lg:-mt-0 -mt-16 flex items-center justify-center">
          <ComposableMap
            projection="geoAlbersUsa"
            projectionConfig={{ scale: 1000 }}
            style={{
              width: "100%",
              height: "auto",
              aspectRatio: "4/3",
            }}
          >
            <Geographies geography={geoUrl}>
              {({ geographies }) => {
                if (!geographies || !Array.isArray(geographies)) {
                  return null;
                }

                return (
                  <>
                    {/* Render all state boundaries */}
                    {geographies.map((geo) => {
                      if (!geo || !geo.properties || !geo.properties.name)
                        return null;

                      const stateAbbr = stateAbbreviations[geo.properties.name];
                      if (!stateAbbr) return null;

                      const isSelected = selectedState === stateAbbr;

                      // Get fill color based on state data or selection
                      let fillColor =
                        (stateFills[stateAbbr] && stateFills[stateAbbr].fill) ||
                        "#FE994F";
                      if (isSelected) {
                        fillColor = "#1B3A5D"; // Changed selected state color to dark blue
                      }

                      return (
                        <Geography
                          key={geo.rsmKey || `geo-${stateAbbr}`}
                          geography={geo}
                          onMouseDown={() => handleStateMouseDown(stateAbbr)}
                          onMouseUp={handleStateMouseUp}
                          style={{
                            default: {
                              fill: fillColor,
                              outline: "none",
                              stroke: "#000000",
                              strokeWidth: 0.6,
                              cursor: "pointer",
                            },
                            // Same as default - no hover effect
                            hover: {
                              fill: fillColor,
                              outline: "none",
                              stroke: "#000000",
                              strokeWidth: 0.6,
                              cursor: "pointer",
                            },
                            pressed: {
                              fill: "#1B3A5D",
                              outline: "none",
                              stroke: "#000000",
                              strokeWidth: 0.6,
                            },
                          }}
                        />
                      );
                    })}

                    {/* Render state labels (for regular states using Markers) */}
                    {geographies.map((geo) => {
                      if (!geo || !geo.properties || !geo.properties.name)
                        return null;

                      const stateAbbr = stateAbbreviations[geo.properties.name];
                      if (!stateAbbr) return null;

                      // Skip selected state - don't render its label
                      if (selectedState === stateAbbr) return null;

                      let centroid = null;
                      try {
                        centroid = geoCentroid(geo);
                      } catch (error) {
                        console.error(
                          `Error calculating centroid for ${stateAbbr}:`,
                          error
                        );
                        return null;
                      }

                      // Skip if no valid centroid or if it's a small state
                      if (
                        !centroid ||
                        !Array.isArray(centroid) ||
                        centroid.length !== 2 ||
                        !isFinite(centroid[0]) ||
                        !isFinite(centroid[1]) ||
                        smallStates.includes(stateAbbr)
                      ) {
                        return null;
                      }

                      // Get adjustments for specific states
                      const adjustment = adjustedLabelPositions[stateAbbr];
                      const adjustedCoordinates = adjustment
                        ? [
                            centroid[0] + adjustment.dx / 25,
                            centroid[1] + adjustment.dy / 25,
                          ]
                        : centroid;

                      return (
                        <Marker
                          key={`marker-${stateAbbr}`}
                          coordinates={adjustedCoordinates}
                        >
                          <text
                            textAnchor="middle"
                            style={{
                              fontFamily: "sans-serif",
                              fontSize: "12px",
                              fontWeight: 600,
                              fill: "#000000",
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              MozUserSelect: "none",
                              msUserSelect: "none",
                              cursor: "pointer",
                            }}
                            onMouseDown={() => handleStateMouseDown(stateAbbr)}
                            onMouseUp={handleStateMouseUp}
                          >
                            {stateAbbr}
                          </text>
                        </Marker>
                      );
                    })}

                    {/* Render annotations for small states */}
                    {geographies.map((geo) => {
                      if (!geo || !geo.properties || !geo.properties.name)
                        return null;

                      const stateAbbr = stateAbbreviations[geo.properties.name];
                      if (!stateAbbr) return null;

                      // Skip selected state - don't render its annotation
                      if (selectedState === stateAbbr) return null;

                      // Only process the small states with line annotations
                      if (
                        !smallStates.includes(stateAbbr) ||
                        stateAbbr === "DC"
                      )
                        return null;

                      let centroid = null;
                      try {
                        centroid = geoCentroid(geo);
                      } catch (error) {
                        console.error(
                          `Error calculating centroid for ${stateAbbr}:`,
                          error
                        );
                        return null;
                      }

                      // Skip if no valid centroid
                      if (
                        !centroid ||
                        !Array.isArray(centroid) ||
                        centroid.length !== 2 ||
                        !isFinite(centroid[0]) ||
                        !isFinite(centroid[1])
                      ) {
                        return null;
                      }

                      const { dx, dy } = getOffsetForState(stateAbbr);

                      return (
                        <Annotation
                          key={`annotation-${stateAbbr}`}
                          subject={centroid}
                          dx={dx}
                          dy={dy}
                          connectorProps={{
                            stroke: "#000000",
                            strokeWidth: 0.8,
                            strokeLinecap: "round",
                          }}
                        >
                          <text
                            x={4}
                            fontSize={12}
                            fontWeight={600}
                            alignmentBaseline="middle"
                            fill="#000000"
                            style={{
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              MozUserSelect: "none",
                              msUserSelect: "none",
                              cursor: "pointer",
                            }}
                            onMouseDown={() => handleStateMouseDown(stateAbbr)}
                            onMouseUp={handleStateMouseUp}
                          >
                            {stateAbbr}
                          </text>
                        </Annotation>
                      );
                    })}

                    {/* Render marker pin for selected state */}
                    {selectedState &&
                      geographies.map((geo) => {
                        if (!geo || !geo.properties || !geo.properties.name)
                          return null;

                        const stateAbbr =
                          stateAbbreviations[geo.properties.name];
                        if (!stateAbbr || stateAbbr !== selectedState)
                          return null;

                        let centroid = null;
                        try {
                          centroid = geoCentroid(geo);
                        } catch (error) {
                          console.error(
                            `Error calculating centroid for ${stateAbbr}:`,
                            error
                          );
                          return null;
                        }

                        // Skip if no valid centroid
                        if (
                          !centroid ||
                          !Array.isArray(centroid) ||
                          centroid.length !== 2 ||
                          !isFinite(centroid[0]) ||
                          !isFinite(centroid[1])
                        ) {
                          return null;
                        }

                        // Apply marker position adjustment (using the same offset system as labels)
                        const markerAdjustment = markerPositions[stateAbbr];
                        const adjustedMarkerPosition = markerAdjustment
                          ? [
                              centroid[0] + markerAdjustment.dx / 25,
                              centroid[1] + markerAdjustment.dy / 25,
                            ]
                          : centroid;

                        return (
                          <Marker
                            key={`pin-${stateAbbr}`}
                            coordinates={adjustedMarkerPosition}
                          >
                            <GisPin />
                          </Marker>
                        );
                      })}
                  </>
                );
              }}
            </Geographies>
          </ComposableMap>
        </div>
      </div>
    </div>
  );
}
